import { useEffect, useRef, useState } from "react";
import { FolderUp, Github, Loader2, Search, FileCode } from "lucide-react";
import api, { API, authedFetch, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

/** Mirrors the Worker's limits so a rejection is caught before the upload. */
const MAX_FILE_BYTES = 512 * 1024;
const MAX_FILES = 200;
const MAX_ARCHIVE_BYTES = 20 * 1024 * 1024;

function readAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsText(file);
  });
}

/**
 * Reports what an import did.
 *
 * Skipped files are shown rather than swallowed — silently dropping half a
 * repository and reporting success is how you get a user debugging a missing
 * file for an hour.
 */
function reportResult(result) {
  const { imported, skipped_count: skippedCount, skipped, truncated } = result;

  if (imported === 0) {
    toast.error("Nothing was imported", {
      description: skipped?.[0]?.reason ?? "No supported text files were found.",
    });
    return;
  }

  const parts = [];
  if (skippedCount > 0) parts.push(`${skippedCount} skipped`);
  if (truncated) parts.push("the repository was too large to read fully");

  toast.success(`Imported ${imported} file${imported === 1 ? "" : "s"}`, {
    description: parts.length > 0 ? parts.join(" · ") : undefined,
  });
}

export default function ImportDialog({ open, onOpenChange, projectId, onImported }) {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [repos, setRepos] = useState(null);
  const [filter, setFilter] = useState("");
  const [manualRepo, setManualRepo] = useState("");

  useEffect(() => {
    if (!open || repos !== null) return;
    api
      .get("/github/repos")
      .then(({ data }) => setRepos(data))
      // Not connected is an ordinary state, not an error worth a toast.
      .catch(() => setRepos([]));
  }, [open, repos]);

  /**
   * Uploads an archive and lets the server unpack it.
   *
   * A zip is how a project actually gets handed around. Before this it went
   * through the loose-file path, where it was one binary well over the
   * per-file limit — so it was skipped, and the dialog then reported that
   * none of the files could be read.
   */
  const importArchive = async (file) => {
    if (file.size > MAX_ARCHIVE_BYTES) {
      toast.error(`${file.name} is larger than ${MAX_ARCHIVE_BYTES / 1024 / 1024} MB`);
      return;
    }

    setBusy(true);
    try {
      const res = await authedFetch(`${API}/projects/${projectId}/import/archive`, {
        method: "POST",
        headers: { "Content-Type": "application/zip" },
        body: file,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));

      reportResult(data);
      if (data.imported > 0) {
        onImported?.();
        onOpenChange(false);
      }
    } catch (err) {
      toast.error(err.message || "The archive could not be imported");
    } finally {
      setBusy(false);
    }
  };

  const importFiles = async (fileList) => {
    const files = [...fileList];
    if (files.length === 0) return;

    // A lone archive is unpacked server-side rather than sent as a file.
    if (files.length === 1 && /\.zip$/i.test(files[0].name)) {
      return importArchive(files[0]);
    }

    if (files.length > MAX_FILES) {
      toast.error(`At most ${MAX_FILES} files at a time`);
      return;
    }

    setBusy(true);
    try {
      const payload = [];
      for (const file of files) {
        if (file.size > MAX_FILE_BYTES) {
          toast.message(`${file.name} is larger than 512 KB and was skipped`);
          continue;
        }
        payload.push({
          // webkitRelativePath preserves the folder structure when a whole
          // directory is picked; a plain file selection has only a name.
          path: file.webkitRelativePath || file.name,
          content: await readAsText(file),
        });
      }
      if (payload.length === 0) {
        toast.error("None of those files could be read");
        return;
      }

      const { data } = await api.post(`/projects/${projectId}/import/files`, { files: payload });
      reportResult(data);
      if (data.imported > 0) {
        onImported?.();
        onOpenChange(false);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const importRepo = async (repo, branch) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/import/github`, { repo, branch });
      reportResult(data);
      if (data.imported > 0) {
        onImported?.();
        onOpenChange(false);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const visible = (repos ?? []).filter((r) =>
    r.full_name.toLowerCase().includes(filter.trim().toLowerCase()),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-app flex items-center gap-2">
            <FolderUp className="w-4 h-4 text-kinetic" /> Import code
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Bring existing files in. Catalina edits them the same way it edits what it generates.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="github" className="w-full min-w-0">
          <TabsList className="grid w-full grid-cols-2 bg-background border border-border">
            <TabsTrigger value="github" data-testid="tab-github" className="font-mono text-xs">
              <Github className="w-3.5 h-3.5 mr-1.5" /> GitHub
            </TabsTrigger>
            <TabsTrigger value="files" data-testid="tab-files" className="font-mono text-xs">
              <FileCode className="w-3.5 h-3.5 mr-1.5" /> Files
            </TabsTrigger>
          </TabsList>

          {/* GitHub */}
          <TabsContent value="github" className="pt-3 space-y-3">
            {repos === null ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-zinc-600" /></div>
            ) : repos.length === 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  No repositories are readable with your token. Connect GitHub in Settings, or type
                  a repository below.
                </p>
                <div className="flex gap-2">
                  <Input
                    data-testid="manual-repo"
                    value={manualRepo}
                    onChange={(e) => setManualRepo(e.target.value)}
                    placeholder="owner/repository"
                    className="bg-background border-border font-mono text-sm"
                  />
                  <Button
                    onClick={() => importRepo(manualRepo.trim())}
                    disabled={busy || !manualRepo.includes("/")}
                    className="bg-white text-black hover:bg-zinc-200"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Import"}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
                  <Input
                    data-testid="repo-filter"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter repositories…"
                    className="bg-background border-border font-mono text-sm pl-8"
                  />
                </div>
                {/*
                  `min-w-0` is load-bearing. A flex item will not shrink below
                  its content's intrinsic width without it, so `truncate` on
                  the repository name did nothing and the longest name pushed
                  this list — and with it the whole dialog — wider than the
                  panel it sits in.
                */}
                <div className="max-h-64 overflow-y-auto border border-border rounded-lg divide-y divide-border min-w-0">
                  {visible.length === 0 ? (
                    <p className="text-xs text-zinc-600 font-mono p-3">No matches.</p>
                  ) : (
                    visible.map((r) => (
                      <button
                        key={r.full_name}
                        data-testid={`repo-${r.full_name}`}
                        disabled={busy}
                        onClick={() => importRepo(r.full_name, r.default_branch)}
                        className="w-full text-left px-3 py-2.5 hover:bg-secondary transition-colors
                                   disabled:opacity-50 focus-visible:outline focus-visible:outline-2
                                   focus-visible:outline-kinetic"
                      >
                        <div className="flex items-center justify-between gap-2 min-w-0">
                          <span className="font-mono text-sm truncate min-w-0">{r.full_name}</span>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {r.private && (
                              <Badge variant="outline" className="border-border text-[10px] font-mono text-zinc-500">
                                private
                              </Badge>
                            )}
                            <span className="text-[10px] font-mono text-zinc-600">{r.default_branch}</span>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
                <p className="text-[11px] text-zinc-600">
                  Dependencies, build output and binaries are skipped.
                </p>
              </>
            )}
          </TabsContent>

          {/* Files */}
          <TabsContent value="files" className="pt-3 space-y-3">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              data-testid="import-file-input"
              onChange={(e) => { importFiles(e.target.files); e.target.value = ""; }}
            />
            <Button
              data-testid="pick-files-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              className="w-full bg-white text-black hover:bg-zinc-200"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileCode className="w-4 h-4 mr-2" /> Choose files or a .zip</>}
            </Button>
            <p className="text-[11px] text-zinc-600">
              A <span className="font-mono">.zip</span> is unpacked for you, up to{" "}
              {MAX_ARCHIVE_BYTES / 1024 / 1024} MB, and its wrapping folder is dropped. Loose files:
              text and code up to 512 KB each, {MAX_FILES} at a time, with the folder structure kept
              when you pick a whole folder. Dependencies, build output, binaries and{" "}
              <span className="font-mono">.env</span> files are skipped either way.
            </p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
